import { useState } from "react";
import axios from "axios";
import { Calendar, CheckCircle2, Send, Mail, Globe, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Panel, PageHead } from "@/components/Panel";
import CalInlineEmbed from "@/components/CalInlineEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sizes = ["1–50", "51–200", "201–1,000", "1,000+"];

const initialForm = {
  name: "",
  work_email: "",
  company: "",
  role: "",
  company_size: "",
  challenge: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please email us at info@revgengenius.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact RevGen Genius — Book a Free RevOps Assessment"
        description="Ready to optimize your revenue operations? Book a free assessment with RevGen Genius or send us a message."
        path="/contact"
      />
      <div className="mx-auto max-w-[1240px] space-y-16 px-5 sm:px-8">
        <PageHead
          testId="contact-head"
          label="Contact"
          title="Let's talk."
          intro="Ready to optimize your revenue operations? Book a free assessment and we'll evaluate where your revenue engine is losing momentum."
        />

        <div className="grid items-start gap-10 lg:grid-cols-2">
          {/* Calendar */}
          <Reveal>
            <Panel label="Book a Free Assessment" className="pt-16">
              <div className="flex items-center gap-3">
                <Calendar size={28} strokeWidth={1.8} className="text-[#1F5FFF]" aria-hidden="true" />
                <h2 className="font-heading text-xl font-semibold">Pick a time that works.</h2>
              </div>

              {/* Live scheduling — Cal.com inline embed (https://cal.com/adam-lightfield-yfl7s8/30min) */}
              <CalInlineEmbed />

              <p className="mt-6 text-center font-heading text-sm font-semibold text-[#0A1F44]">
                30 minutes. No cost. No obligation.
              </p>
            </Panel>
          </Reveal>

          {/* Message form */}
          <Reveal delay={0.1}>
            <Panel label="Send a Message" tint className="pt-16">
              {submitted ? (
                <div data-testid="contact-success-message" className="flex min-h-[420px] flex-col items-center justify-center gap-5 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#1F5FFF]">
                    <CheckCircle2 size={30} className="text-white" aria-hidden="true" />
                  </span>
                  <h2 className="font-heading text-2xl font-semibold">Thanks — we've got it.</h2>
                  <p className="max-w-sm leading-relaxed text-[#5B6784]">
                    Someone from the team will be in touch within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="field-label">Full Name</label>
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Jordan Reyes"
                        className="field"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="field-label">Work Email</label>
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        required
                        value={form.work_email}
                        onChange={update("work_email")}
                        placeholder="jordan@company.com"
                        className="field"
                      />
                    </div>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-company" className="field-label">Company</label>
                      <input
                        id="contact-company"
                        data-testid="contact-company-input"
                        type="text"
                        required
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Company, Inc."
                        className="field"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-role" className="field-label">Role</label>
                      <input
                        id="contact-role"
                        data-testid="contact-role-input"
                        type="text"
                        required
                        value={form.role}
                        onChange={update("role")}
                        placeholder="VP of Sales"
                        className="field"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-size" className="field-label">Company Size</label>
                    <select
                      id="contact-size"
                      data-testid="contact-size-select"
                      required
                      value={form.company_size}
                      onChange={update("company_size")}
                      className="field"
                    >
                      <option value="" disabled>Select company size</option>
                      {sizes.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-challenge" className="field-label">
                      What's your biggest revenue operations challenge?
                    </label>
                    <textarea
                      id="contact-challenge"
                      data-testid="contact-challenge-textarea"
                      required
                      rows={4}
                      value={form.challenge}
                      onChange={update("challenge")}
                      placeholder="Tell us where the revenue engine is stalling."
                      className="field resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : (<>Send Message <Send size={17} /></>)}
                  </button>
                </form>
              )}
            </Panel>
          </Reveal>
        </div>

        <Reveal>
          <Panel label="Direct Contact" className="bg-[#F8FBFF] pt-16">
            <div className="grid gap-10 sm:grid-cols-3">
              <a
                href="mailto:info@revgengenius.com"
                data-testid="direct-contact-email"
                className="group flex items-start gap-4"
              >
                <Mail size={28} strokeWidth={1.8} className="mt-0.5 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                <div>
                  <p className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[#5B6784]">Email</p>
                  <p className="mt-1.5 font-semibold text-[#0A1F44] transition-colors group-hover:text-[#1F5FFF]">
                    info@revgengenius.com
                  </p>
                </div>
              </a>
              <a
                href="https://www.revgengenius.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="direct-contact-web"
                className="group flex items-start gap-4"
              >
                <Globe size={28} strokeWidth={1.8} className="mt-0.5 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                <div>
                  <p className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[#5B6784]">Web</p>
                  <p className="mt-1.5 font-semibold text-[#0A1F44] transition-colors group-hover:text-[#1F5FFF]">
                    www.revgengenius.com
                  </p>
                </div>
              </a>
              <a
                href="https://www.linkedin.com/company/revgengenius"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="direct-contact-linkedin"
                className="group flex items-start gap-4"
              >
                <Linkedin size={28} strokeWidth={1.8} className="mt-0.5 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                <div>
                  <p className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[#5B6784]">LinkedIn</p>
                  <p className="mt-1.5 font-semibold text-[#0A1F44] transition-colors group-hover:text-[#1F5FFF]">
                    RevGen Genius
                  </p>
                </div>
              </a>
            </div>
          </Panel>
        </Reveal>
      </div>
    </div>
  );
}
